import { Text, Flex, IconProps } from '@everlywell/ui-kit';
import DynamicIcon, { DynamicIconProps, IconKey } from 'components/DynamicIcon';
import React from 'react';

export type BulletPointWithIconProps = {
  iconName?: string | null;
  iconColor?: string | null;
  text?: string | null;
  textColor?: string | null;
  weight?: DynamicIconProps['weight'];
  /**
   * Sets the size of the icon in pixels. Default is 24px.
   */
  iconFontSize?: number;
  /**
   * Sets the size of the icon box in pixels. Default is 32px.
   */
  backgroundSize?: number;
  /*
   * If true, the icon will have round background. Default is false.
   */
  hasRoundIconBackground?: boolean;
  iconBackgroundColor?: IconProps['backgroundColor'] | null;
  sx?: any;
};

/**
 * BulletPointWithIcon component is used to display a bullet point with an icon
 * @param {BulletPointWithIconProps} props BulletPointWithIconProps object containing iconName and text
 * @returns {JSX.Element} containing bullet point with icon
 * @example
 * <BulletPointWithIcon iconName="check" text="Check" />
 *
 */
const BulletPointWithIcon = ({
  iconName,
  iconColor,
  text,
  textColor,
  weight,
  hasRoundIconBackground,
  iconBackgroundColor,
  backgroundSize = 32,
  iconFontSize = 24,
  sx,
}: BulletPointWithIconProps): JSX.Element => (
  <Flex gap="2" alignItems="center" sx={sx}>
    {iconName ? (
      <DynamicIcon
        icon={iconName as IconKey}
        color={iconColor}
        weight={weight}
        backgroundColor={iconBackgroundColor || undefined}
        {...(hasRoundIconBackground
          ? {
            width: backgroundSize,
            height: backgroundSize,
            borderRadius: 'full',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }
          : {})}
        sx={{
          '&:before': {
            fontSize: iconFontSize,
          },
        }}
      />
    ) : null}
    {text ? (
      <Text fontSize={['md', null, 'lg']} color={textColor || undefined}>
        {text}
      </Text>
    ) : null}
  </Flex>
);

export default BulletPointWithIcon;
